import { useEffect, useRef, useState } from 'react'
import isEqual from 'lodash/isEqual'
import Api from '@vacationtracker/shared/services/api'
import logger from '@vacationtracker/shared/functions/logger'
import { IGoogleDirectoryChannelStatus } from '@vacationtracker/shared/types/google'

export interface IUseGoogleDirectoryConnectionParams {
  enabled: boolean
  onTokenExpired: () => void
  onRefreshSuccess: () => void
  onRefreshError: () => void
}

export interface IUseGoogleDirectoryConnectionResult {
  channels: IGoogleDirectoryChannelStatus[] | null
  isRefreshing: boolean
  refresh: () => Promise<void>
}

// Channels are created by the import-users-automatically lambda, downstream of COMPANY_SETTINGS_UPDATED,
// so the read taken the moment an admin turns auto-import on beats the rows into existence. Re-read for
// a while after that one transition so the status settles without a page reload. Nothing else polls:
// a missing row is a real "not connected", not a sign that anything is on its way.
const PROVISIONING_TIMEOUT_MS = 120000
const PROVISIONING_MIN_DELAY_MS = 2000
const PROVISIONING_MAX_DELAY_MS = 10000
// Give the lambda a head start, so the first read after the switch is not a guaranteed miss
const PROVISIONING_HEAD_START_MS = 3000
// A read that keeps failing will not start succeeding inside the window, so stop asking
const PROVISIONING_MAX_FAILURES = 2

export const useGoogleDirectoryConnection = (params: IUseGoogleDirectoryConnectionParams): IUseGoogleDirectoryConnectionResult => {
  const { enabled, onTokenExpired, onRefreshSuccess, onRefreshError } = params
  const [channels, setChannels] = useState<IGoogleDirectoryChannelStatus[] | null>(null)
  const [isRefreshing, setIsRefreshing] = useState(false)
  const wasEnabled = useRef(enabled)

  useEffect(() => {
    const justTurnedOn = enabled && !wasEnabled.current
    wasEnabled.current = enabled

    if (!enabled) {
      setChannels(null)
      return
    }

    let ignore = false
    let timer: ReturnType<typeof setTimeout> | undefined
    let delay = PROVISIONING_MIN_DELAY_MS
    let budgetLeftMs = PROVISIONING_TIMEOUT_MS
    let failures = 0

    const pollAgain = () => {
      if (!justTurnedOn || ignore || delay > budgetLeftMs) {
        return
      }
      budgetLeftMs -= delay
      timer = setTimeout(load, delay)
      delay = Math.min(delay * 2, PROVISIONING_MAX_DELAY_MS)
    }

    async function load() {
      try {
        const response = await Api.get<{ channels: IGoogleDirectoryChannelStatus[] }>('/google/webhook-channels')
        if (ignore) {
          return
        }
        failures = 0
        // Hold the identity steady so a poll that changes nothing does not re-render the page
        setChannels((previous) => (isEqual(previous, response.channels) ? previous : response.channels))
        // expiresAt is null only when the row is missing, so this stops once the lambda has caught up
        if (response.channels.some((channel) => channel.expiresAt === null)) {
          pollAgain()
        }
      } catch (error) {
        logger.error('Failed to load Google directory connection status', error)
        failures++
        if (failures <= PROVISIONING_MAX_FAILURES) {
          pollAgain()
        }
      }
    }

    if (justTurnedOn) {
      timer = setTimeout(load, PROVISIONING_HEAD_START_MS)
    } else {
      void load()
    }

    return () => {
      ignore = true
      clearTimeout(timer)
    }
  }, [enabled])

  const refresh = async () => {
    setIsRefreshing(true)
    try {
      const response = (await Api.post('/google/webhook-channels/refresh', {})) as { channels: IGoogleDirectoryChannelStatus[] }
      setChannels(response.channels)
      // The endpoint answers 200 with disconnected entries for the events it could not rebuild
      if (response.channels.every((channel) => channel.connected)) {
        onRefreshSuccess()
      } else {
        onRefreshError()
      }
    } catch (error: unknown) {
      // Api rejects with the parsed response body, not an HTTP status
      const err = error as { response?: { data?: { error?: string } } }
      logger.error('Failed to refresh Google directory connection', error)
      if (err.response?.data?.error === 'GOOGLE_TOKEN_EXPIRED') {
        onTokenExpired()
      } else {
        onRefreshError()
      }
    } finally {
      setIsRefreshing(false)
    }
  }

  return { channels, isRefreshing, refresh }
}
